import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const ICONS = {
  success: <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />,
  error: <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />,
  info: <Info className="w-4 h-4 text-zinc-300 shrink-0" />,
};

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [progress, setProgress] = useState(100);
  const duration = toast.duration ?? 4500;

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        onDismiss(toast.id);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [toast.id, duration, onDismiss]);

  return (
    <div
      className="relative w-full max-w-sm rounded-xl border border-zinc-800 bg-zinc-900/95 backdrop-blur-md shadow-xl overflow-hidden text-left"
    >
      <div className="p-3.5 flex items-start gap-3">
        <div className="mt-0.5">{ICONS[toast.type]}</div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-white">{toast.title}</p>
          {toast.message && (
            <p className="text-[11px] text-zinc-400 mt-0.5 leading-normal">{toast.message}</p>
          )}
        </div>
        <button
          onClick={() => onDismiss(toast.id)}
          className="text-zinc-500 hover:text-zinc-300 transition-colors shrink-0 p-0.5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="h-0.5 bg-zinc-800">
        <div
          className="h-full bg-zinc-500 transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

type ToastListener = (toast: Toast) => void;
const listeners: ToastListener[] = [];

export const toast = {
  success: (title: string, message?: string, duration?: number) =>
    emit({ type: 'success', title, message, duration }),
  error: (title: string, message?: string, duration?: number) =>
    emit({ type: 'error', title, message, duration: duration ?? 6000 }),
  warning: (title: string, message?: string, duration?: number) =>
    emit({ type: 'warning', title, message, duration }),
  info: (title: string, message?: string, duration?: number) =>
    emit({ type: 'info', title, message, duration }),
};

function emit(t: Omit<Toast, 'id'>) {
  const toast: Toast = { ...t, id: crypto.randomUUID() };
  listeners.forEach(fn => fn(toast));
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const handler: ToastListener = (t) => {
      setToasts(prev => [...prev, t]);
    };
    listeners.push(handler);
    return () => {
      const idx = listeners.indexOf(handler);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 items-end pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto w-full">
          <ToastItem toast={t} onDismiss={dismiss} />
        </div>
      ))}
    </div>
  );
}
